import * as React from 'react';
import { get, isUndefined } from 'lodash';

import { FIELD_PATHS, FIELD_VALUES, FREQUENCY, InputType } from '@funnels/constants';
import {
  calculateInterestRate,
  calculateLoanTerm,
  calculateRepaymentValue,
  convertYearsToMonths,
  convertYearsToYearsMonths,
  formatToPrecision,
  getLiabilityBalanceForRepayment,
} from '@funnels/utils';

import { useFormValues } from '../useFormValues';
import { UseRepaymentCalculator } from './types';

interface Data {
  balance?: number;
  interestRate?: number;
  repaymentType?: string;
  repaymentAmount?: number;
  repaymentFrequency?: FREQUENCY;
  remainingTerm?: number;
  interestOnlyRemainingTerm?: number;
}

function calculate(toCalculate: InputType, data: Data): number | undefined {
  if (isUndefined(data.repaymentType)) {
    return;
  }

  const remainingMonths = !isUndefined(data.remainingTerm) ? convertYearsToMonths(data.remainingTerm) : undefined;
  const interestOnlyRemainingMonths = !isUndefined(data.interestOnlyRemainingTerm)
    ? convertYearsToMonths(data.interestOnlyRemainingTerm)
    : undefined;
  const extraContribution = 0;

  switch (toCalculate) {
    case InputType.REMAINING_TERM:
      return calculateLoanTerm(
        data.balance,
        data.interestRate,
        data.repaymentAmount,
        extraContribution,
        data.repaymentFrequency
      );
    case InputType.MINIMUM_REPAYMENTS:
      return calculateRepaymentValue(
        data.balance,
        data.interestRate,
        remainingMonths,
        interestOnlyRemainingMonths,
        data.repaymentFrequency
      );
    case InputType.INTEREST_RATE:
    default:
      return calculateInterestRate(
        data.balance,
        remainingMonths,
        data.repaymentAmount,
        data.repaymentFrequency,
        interestOnlyRemainingMonths
      );
  }
}

function format(output: InputType, value?: number): string {
  let displayValue;

  switch (output) {
    case InputType.INTEREST_RATE:
      displayValue = isUndefined(value) ? '--' : formatToPrecision(value, 2).toString();

      return `${displayValue}%`;

    case InputType.REMAINING_TERM:
      let years: string | number = '--';
      let months: string | number = '--';

      if (!isUndefined(value)) {
        ({ years, months } = convertYearsToYearsMonths(value));
      }

      return `${years} years & ${months} months`;

    case InputType.MINIMUM_REPAYMENTS:
      displayValue = isUndefined(value) ? '--' : formatToPrecision(value, 2).toString();

      return `$${displayValue}`;

    default:
      return '--';
  }
}

export function useRepaymentCalculator(liabilityIndex: number): UseRepaymentCalculator {
  const [input1, setInput1] = React.useState<InputType>(InputType.REMAINING_TERM);
  const [input2, setInput2] = React.useState<InputType>(InputType.MINIMUM_REPAYMENTS);
  const output = [InputType.REMAINING_TERM, InputType.MINIMUM_REPAYMENTS, InputType.INTEREST_RATE].filter(
    (input) => input !== input1 && input !== input2
  )[0];
  const [currentValues, setValue] = useFormValues();
  const index = liabilityIndex;
  const repaymentType = get(
    currentValues,
    FIELD_PATHS.LIABILITY_REPAYMENT_TYPE(index),
    FIELD_VALUES.PRINCIPAL_AND_INTEREST
  );

  const balance = getLiabilityBalanceForRepayment(liabilityIndex, currentValues);
  const remainingTerm = get(currentValues, FIELD_PATHS.LIABILITY_REMAINING_TERM(index));
  const interestOnlyRemainingTerm = get(currentValues, FIELD_PATHS.LIABILITY_INTEREST_ONLY_REMAINING_TERM(index));
  const repaymentAmount = get(currentValues, FIELD_PATHS.LIABILITY_REPAYMENT_VALUE(index));
  const repaymentFrequency = get(currentValues, FIELD_PATHS.LIABILITY_REPAYMENT_FREQUENCY(index), FREQUENCY.MONTHLY);
  const interestRate = get(currentValues, FIELD_PATHS.LIABILITY_INTEREST_RATE(index));
  // clearing the interest rate when it gets calculated to unrealistic values
  React.useEffect(() => {
    if (isNaN(interestRate) || interestRate > 99.99 || interestRate < 0) {
      setValue(FIELD_PATHS.LIABILITY_INTEREST_RATE(index), undefined);
    }
  }, [interestRate]);
  const data = {
    balance,
    remainingTerm,
    interestOnlyRemainingTerm,
    repaymentType,
    repaymentAmount,
    repaymentFrequency,
    interestRate,
  };
  const calculated = calculate(output, data);
  const formatted = format(output, calculated);

  // (Re)setting things when changing of repayment type (Principal & Interest or Interest Only)
  React.useEffect(() => {
    if (repaymentType === (FIELD_VALUES.INTEREST_ONLY as string)) {
      setInput1(InputType.REMAINING_TERM);

      if (input2 === InputType.REMAINING_TERM) {
        setInput2(InputType.MINIMUM_REPAYMENTS);
      }
    } else {
      setValue(FIELD_PATHS.LIABILITY_INTEREST_ONLY_REMAINING_TERM(index), undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repaymentType]);

  // Set repayment frequency to "MONTHLY" when the desired calculated output is the minimum repayment amout.
  React.useEffect(() => {
    if (output !== InputType.MINIMUM_REPAYMENTS || !isUndefined(repaymentFrequency)) {
      return;
    }

    setValue(FIELD_PATHS.LIABILITY_REPAYMENT_FREQUENCY(index), FREQUENCY.MONTHLY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [output]);

  // Updating the state with the desired calculated output value.
  React.useEffect(() => {
    if (output === InputType.INTEREST_RATE) {
      setValue(FIELD_PATHS.LIABILITY_INTEREST_RATE(index), calculated);
    } else if (output === InputType.MINIMUM_REPAYMENTS) {
      setValue(FIELD_PATHS.LIABILITY_REPAYMENT_VALUE(index), calculated);
    } else if (output === InputType.REMAINING_TERM) {
      setValue(FIELD_PATHS.LIABILITY_REMAINING_TERM(index), calculated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculated, output]);

  return [input1, input2, output, formatted, setInput1, setInput2];
}
